/**
 * IBAN / BIC helpers (ISO 13616 / ISO 9362). Client-safe, no dependencies.
 *
 * An IBAN is valid when its country is known, its length matches that country and its
 * check digits (02–98) pass mod-97. The per-country BBAN structure is not checked: mod-97 already
 * catches typos and transpositions, which is what we guard against here.
 */

/** IBAN length per country (SWIFT IBAN registry plus national IBAN formats). */
const IBAN_LENGTHS: Record<string, number> = {
  AD: 24,
  AE: 23,
  AL: 28,
  AO: 25,
  AT: 20,
  AX: 18,
  AZ: 28,
  BA: 20,
  BE: 16,
  BF: 28,
  BG: 22,
  BH: 22,
  BI: 27,
  BJ: 28,
  BL: 27,
  BR: 29,
  BY: 28,
  CF: 27,
  CG: 27,
  CH: 21,
  CI: 28,
  CM: 27,
  CR: 22,
  CV: 25,
  CY: 28,
  CZ: 24,
  DE: 22,
  DJ: 27,
  DK: 18,
  DO: 28,
  DZ: 26,
  EE: 20,
  EG: 29,
  ES: 24,
  FI: 18,
  FK: 18,
  FO: 18,
  FR: 27,
  GA: 27,
  GB: 22,
  GE: 22,
  GF: 27,
  GI: 23,
  GL: 18,
  GP: 27,
  GQ: 27,
  GR: 27,
  GT: 28,
  GW: 25,
  HN: 28,
  HR: 21,
  HU: 28,
  IE: 22,
  IL: 23,
  IQ: 23,
  IR: 26,
  IS: 26,
  IT: 27,
  JO: 30,
  KM: 27,
  KW: 30,
  KZ: 20,
  LB: 28,
  LC: 32,
  LI: 21,
  LT: 20,
  LU: 20,
  LV: 21,
  LY: 25,
  MA: 28,
  MC: 27,
  MD: 24,
  ME: 22,
  MF: 27,
  MG: 27,
  MK: 19,
  ML: 28,
  MN: 20,
  MQ: 27,
  MR: 27,
  MT: 31,
  MU: 30,
  MZ: 25,
  NC: 27,
  NE: 28,
  NI: 28,
  NL: 18,
  NO: 15,
  OM: 23,
  PF: 27,
  PK: 24,
  PL: 28,
  PM: 27,
  PS: 29,
  PT: 25,
  QA: 29,
  RE: 27,
  RO: 24,
  RS: 22,
  RU: 33,
  SA: 24,
  SC: 31,
  SD: 18,
  SE: 24,
  SI: 19,
  SK: 24,
  SM: 27,
  SN: 28,
  SO: 23,
  ST: 25,
  SV: 28,
  TD: 27,
  TF: 27,
  TG: 28,
  TL: 23,
  TN: 24,
  TR: 26,
  UA: 29,
  VA: 22,
  VG: 24,
  WF: 27,
  XK: 20,
  YE: 30,
  YT: 27,
};

const IBAN_SHAPE = /^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/;

/** Bank (4 letters), country (2 letters), location (2), optional branch (3). */
const BIC_SHAPE = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

/** Mod-97 of the IBAN with its first four characters moved to the end and letters as 10–35. */
function ibanMod97(iban: string): number {
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const char of rearranged) {
    const value = char >= 'A' ? char.charCodeAt(0) - 55 : Number(char);
    // Letters expand to two digits; feed them in one step to keep the remainder small.
    remainder = (remainder * (value > 9 ? 100 : 10) + value) % 97;
  }
  return remainder;
}

/** Electronic format: no spaces or separators, uppercase. Empty input → null. */
export function normalizeIban(value: string | null | undefined): string | null {
  const iban = (value ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  return iban || null;
}

/** BIC without spaces, uppercase. Empty input → null. */
export function normalizeBic(value: string | null | undefined): string | null {
  const bic = (value ?? '').replace(/\s/g, '').toUpperCase();
  return bic || null;
}

/** Valid IBAN (known country, its length, mod-97 check digits), whatever its spacing. */
export function isValidIban(value: string | null | undefined): boolean {
  const iban = normalizeIban(value);
  if (!iban || !IBAN_SHAPE.test(iban) || IBAN_LENGTHS[iban.slice(0, 2)] !== iban.length) {
    return false;
  }
  // Check digits are 02–98; 00, 01 and 99 can pass mod-97 but are never issued.
  const checkDigits = Number(iban.slice(2, 4));
  return checkDigits >= 2 && checkDigits <= 98 && ibanMod97(iban) === 1;
}

/** Empty BICs are allowed (SEPA doesn't need one); a filled-in BIC must be well-formed. */
export function isValidOptionalBic(value: string | null | undefined): boolean {
  const bic = normalizeBic(value);
  return bic == null || BIC_SHAPE.test(bic);
}

/** `BE68 5390 0754 7034` — for documents and screens. */
export function formatIban(value: string | null | undefined): string {
  const iban = normalizeIban(value);
  return iban ? iban.replace(/(.{4})(?=.)/g, '$1 ') : '';
}

/** ISO country of the IBAN (`BE68…` → `BE`). */
export function getIbanCountry(value: string | null | undefined): string | null {
  const iban = normalizeIban(value);
  return iban ? iban.slice(0, 2) : null;
}
